/**
 * The five feedback loops, ported from `OEM+_Platform_Demo.html`.
 *
 * Source lines
 * ------------
 * - `LOOPS` — demo lines 2914–2938 (5 loops). `LOOP_BY` is demo line 2939.
 *
 * What changed in the port
 * ------------------------
 * 1. **`items()` became data.** Identical treatment to `modules.ts`: the tuple
 *    `[what, where, figure, handler, status]` is now a named-field
 *    {@link LoopItem}, the handler string is a {@link SeedNav}, and each `${…}`
 *    inside the figure template is kept as a `{ expr }` {@link FigureSegment}
 *    rather than being evaluated or baked in. Literal text is verbatim.
 *
 * 2. **`table()` returned an HTML string** built from live data
 *    (`EXCHANGE`, `HEALTH`, `B.bulletins`, `B.cases`, `FLEET_RULES`,
 *    `renewals()`, `upgradeCandidates()`, `predictedUsage()`). HTML cannot
 *    cross into a React scene, so each panel is described structurally by
 *    {@link LoopPanel}: its eyebrow and column headers are carried verbatim
 *    (they are human-readable text), the row derivation is carried as an
 *    opaque `rowsExpr` for the scene lane to reimplement against its own data,
 *    and any string the demo's row builder emitted conditionally is listed in
 *    `literals` so no wording is lost.
 *
 *    Markup stripped from the panels: `<div class="card pad">`, `<table
 *    class="t">`, `<span class="pill …">`, `<button class="src">` and the
 *    inline `style` attributes. None of it carried meaning beyond alignment,
 *    which survives as {@link LoopColumn.numeric}, and the band/status pill,
 *    which the scene lane derives from the row itself.
 *
 * 3. **No handler strings remain.** The inline `show('machine','…')` handlers
 *    inside the table builders became the scene lane's concern; the only
 *    navigation kept as data is the per-item `nav`.
 *
 * Data Schema layer — pure data and pure types. No React, no side effects.
 */

import {
  ft,
  fx,
  navAsk,
  navAudit,
  navDoc,
  navShow,
  type LiveFigure,
  type ModuleStatus,
  type SeedNav,
} from './modules';

/** One column header of a loop panel. */
export interface LoopColumn {
  /** Header text, verbatim from the demo `<th>`. */
  readonly label: string;
  /** The demo right-aligned this column (`style="text-align:right"`). */
  readonly numeric?: boolean;
}

/**
 * One panel below a loop's item list.
 *
 * `rowsExpr` / `chipsExpr` carry the demo's derivation verbatim as an opaque
 * string. Nothing here evaluates them; they document exactly which dataset and
 * filter produced the panel so the scene lane can rebuild it.
 */
export type LoopPanel =
  | {
      readonly kind: 'table';
      /** Panel eyebrow, verbatim. */
      readonly eyebrow: string;
      readonly cols: readonly LoopColumn[];
      /** Demo row derivation, verbatim source. */
      readonly rowsExpr: string;
      /** Strings the demo's row builder emitted conditionally, verbatim. */
      readonly literals?: readonly string[];
    }
  | {
      readonly kind: 'chips';
      /** Panel eyebrow, verbatim. */
      readonly eyebrow: string;
      /** Demo chip-list derivation, verbatim source. */
      readonly chipsExpr: string;
      /** How the demo truncated each chip label. */
      readonly note?: string;
    };

/** One row of a loop's item list. */
export interface LoopItem {
  /** What the operating-model poster says. */
  readonly what: string;
  /** Where it lives in the platform. */
  readonly where: string;
  /** The live figure this morning, as a template. */
  readonly figure: LiveFigure;
  /** Where the row goes when clicked. */
  readonly nav: SeedNav;
  /** Delivery status of the row. */
  readonly status: ModuleStatus;
}

/** Feedback-loop ids, in demo order. */
export const LOOP_IDS = ['component', 'warranty', 'learning', 'commercial', 'planning'] as const;

/** Closed set of feedback-loop ids. */
export type LoopId = (typeof LOOP_IDS)[number];

/** One of the five feedback loops. */
export interface OemLoop {
  readonly id: LoopId;
  /** Loop number, 1–5. */
  readonly n: number;
  /** Full name. */
  readonly name: string;
  /** Owning agent name (key into `agents.ts`). */
  readonly agent: string;
  /** Loop swatch. */
  readonly col: string;
  /** Inline SVG path data for the loop glyph (viewBox `0 0 24 24`). */
  readonly icon: string;
  /** The long-form description. */
  readonly purpose: string;
  /** Item rows — demo `items()`. */
  readonly items: readonly LoopItem[];
  /** Panels below the list — demo `table()`, destructured. */
  readonly panels: readonly LoopPanel[];
}

/** Demo lines 2914–2938 — `const LOOPS=[…]`, five records in demo order. */
export const LOOPS: readonly OemLoop[] = [
  {
    id: 'component',
    n: 1,
    name: 'Component Lifecycle & Rebuild',
    agent: 'Component Lifecycle',
    col: '#2F5FA8',
    icon: '<path d="M4 12a8 8 0 0114-5.3M20 12a8 8 0 01-14 5.3"/><path d="M18 3v4h-4M6 21v-4h4"/>',
    purpose:
      'Serialised components are tracked from install to removal: what is coming up on its interval, which exchange unit is in the pool, where the core goes back to, what the workshop rebuilds and tests, and the history each serial carries into its next life.',
    items: [
      {
        what: 'Track serialized components',
        where: 'Maximo sub-assets (install date, hours since change, condition)',
        figure: [
          fx('Object.values(B.mx).reduce((a,m)=>a+(m.children||[]).length,0)'),
          ft(' sub-assets on '),
          fx('Object.keys(B.mx).length'),
          ft(' records'),
        ],
        nav: navShow('machine', 'L-18', 'record'),
        status: 'live',
      },
      {
        what: 'Predict removal / replacement',
        where: 'Components past interval or inside a failure window',
        figure: [
          fx('pastInterval().length'),
          ft(' past interval · '),
          fx("HEALTH.filter(h=>h.band==='major'||h.band==='vpoor').length"),
          ft(' in the very poor or major bands'),
        ],
        nav: navShow('fleet'),
        status: 'live',
      },
      {
        what: 'Manage exchange pools',
        where: 'Exchange units on the open jobs',
        figure: [fx('EXCHANGE.length'), ft(' exchange units in play (table below)')],
        nav: navShow('loops', 'component'),
        status: 'live',
      },
      {
        what: 'Core return & reverse logistics',
        where: 'Core back on the same truck / air',
        figure: [
          ft('core routing on the exchange units · reverse-logistics tracking on the roadmap'),
        ],
        nav: navShow('partsflow'),
        status: 'road',
      },
      {
        what: 'Workshop repair / rebuild / test',
        where: 'Adelaide rebuild queue · HV workshop',
        figure: [
          ft(
            'L-18 turbo core and the L-07 battery module in the teardown queue · bay capacity on the roadmap',
          ),
        ],
        nav: navShow('loops', 'component'),
        status: 'road',
      },
      {
        what: 'Update component history',
        where: 'Hours since change reset on fit',
        figure: [
          ft('every change-out resets hours since change on the record and the condition register'),
        ],
        nav: navAudit('health'),
        status: 'live',
      },
    ],
    panels: [
      {
        kind: 'table',
        eyebrow: 'Exchange units this fortnight',
        cols: [
          { label: 'Part' },
          { label: 'Exchange unit' },
          { label: 'Job' },
          { label: 'Where it is · core' },
        ],
        rowsExpr: 'EXCHANGE.map(e=>[e[0],e[1],e[2],e[3]])',
      },
      {
        kind: 'table',
        eyebrow: 'Components closest to their change interval · next 30 days of running',
        cols: [
          { label: 'Machine' },
          { label: 'Component' },
          { label: 'Hours since change', numeric: true },
          { label: 'Interval', numeric: true },
          { label: 'Remaining', numeric: true },
          { label: 'Condition' },
        ],
        rowsExpr:
          'HEALTH.map(h=>({...h,iv:intervalOf(h.component),rem:intervalOf(h.component)-h.hours_since_change})).filter(h=>h.rem<400).sort((a,b)=>a.rem-b.rem).slice(0,16)',
        literals: ['overdue '],
      },
    ],
  },

  {
    id: 'warranty',
    n: 2,
    name: 'Warranty, Quality & Campaigns',
    agent: 'Warranty & Quality',
    col: '#2E7D4F',
    icon: '<path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"/><path d="M9 12l2 2 4-4"/>',
    purpose:
      'Warranty claims are processed against the evidence on the record; failures are clustered across the fleet; root causes go to engineering review; bulletins and campaigns come out of it with the parts and labour planned; and campaign execution is tracked machine by machine.',
    items: [
      {
        what: 'Process warranty claims',
        where: 'Cases · Salesforce',
        figure: [
          fx('B.cases.filter(c=>/Warranty/.test(c.type)).length'),
          ft(
            ' warranty cases: CS-2026-2210 declined on the oil evidence, CS-2026-2216 under assessment (L-07 module)',
          ),
        ],
        nav: navAudit('cases', [{ field: 'type', op: 'matches', value: 'Warranty' }]),
        status: 'live',
      },
      {
        what: 'Identify failure clusters',
        where: 'Fleet learning · repeat failures',
        figure: [
          ft(
            '2 early coupling failures at Kalbarri · 4 ML-21 hose kits past interval at Gawler Deep · charging connectors on the ML-21E',
          ),
        ],
        nav: navShow('incident'),
        status: 'live',
      },
      {
        what: 'Root cause analysis',
        where: 'Incident reports · replays',
        figure: [
          fx('INCIDENTS.length'),
          ft(' incidents with a root cause and the data that preceded them'),
        ],
        nav: navShow('incident'),
        status: 'live',
      },
      {
        what: 'Engineering review',
        where: 'Reliability review Thursday 14:00 · Teams',
        figure: [fx('B.meetings.length'), ft(' recorded reviews and debriefs, transcribed')],
        nav: navDoc('mtg:MTG-2026-0908B.txt'),
        status: 'live',
      },
      {
        what: 'Create bulletins / campaigns',
        where: 'OEM bulletin service',
        figure: [
          fx('B.bulletins.length'),
          ft(' bulletins in force · 3 issued this fortnight (TB-2026-033, SB-2026-004, TB-2026-034)'),
        ],
        nav: navShow('data'),
        status: 'live',
      },
      {
        what: 'Plan parts & labour',
        where: 'Campaign work orders',
        figure: [
          ft('hose campaign across the ML-21 fleet at Gawler Deep — WO-26-11916 first, two weekends'),
        ],
        nav: navAudit('wos', [{ field: 'wo_id', op: 'eq', value: 'WO-26-11916' }]),
        status: 'drafts',
      },
      {
        what: 'Track campaign execution',
        where: 'Per-machine acknowledgement and completion',
        figure: [
          ft(
            '1 of 4 hose kits fitted (L-18) · bulletin acknowledgement tracked against each affected serial',
          ),
        ],
        nav: navShow('customers'),
        status: 'live',
      },
    ],
    panels: [
      {
        kind: 'chips',
        eyebrow: 'Bulletins in force · click to open the PDF',
        chipsExpr: "B.bulletins.map(b=>({id:b.id,title:b.title,doc:'x:'+b.file}))",
        note: 'The demo truncated a title longer than 52 characters to its first 50 plus an ellipsis.',
      },
      {
        kind: 'table',
        eyebrow: 'Warranty & campaign cases',
        cols: [
          { label: 'Case' },
          { label: 'Machine' },
          { label: 'Type' },
          { label: 'Status' },
          { label: 'Subject' },
        ],
        rowsExpr: 'B.cases.filter(c=>/Warranty|Incident/.test(c.type))',
      },
    ],
  },

  {
    id: 'learning',
    n: 3,
    name: 'Fleet Learning & Continuous Improvement',
    agent: 'Fleet Learning',
    col: '#5E35B1',
    icon: '<path d="M4 20V10M10 20V4M16 20v-8M22 20H2"/>',
    purpose:
      'Every completed work order and every incident is mined for the rule the whole fleet should run on: a better failure model, a changed maintenance plan, a sharper parts forecast, guidance for the next technician, feedback to product engineering and a tighter inventory policy.',
    items: [
      {
        what: 'Analyse completed work orders',
        where: 'Register · 18 months',
        figure: [
          fx('WOS.filter(w=>/^Closed/.test(w.status)).length.toLocaleString()'),
          ft(' closed work orders analysed for cause, crew and cost'),
        ],
        nav: navShow('costs'),
        status: 'live',
      },
      {
        what: 'Improve failure models',
        where: 'Predictive risk ranking',
        figure: [
          ft('risk ranking across '),
          fx('FLEET.length'),
          ft(' machines from condition, drift, breakdowns, late PMs and abuse events'),
        ],
        nav: navAsk('Which machines are most likely to fail next and why?'),
        status: 'live',
      },
      {
        what: 'Update maintenance plans',
        where: 'PM intervals · sampling rules',
        figure: [
          ft(
            '250 h oil sampling on the ML-21 · contract PM plans reconciled to OEM intervals · hose inspection added to PM-250',
          ),
        ],
        nav: navAudit('wos', [
          { field: 'wo_type', op: 'eq', value: 'Preventive' },
          { field: 'status', op: 'eq', value: 'Scheduled' },
        ]),
        status: 'live',
      },
      {
        what: 'Refine parts forecasts',
        where: 'Predicted usage → minimums',
        figure: [
          fx('predictedUsage().length'),
          ft(' minimum-level proposals from flagged components'),
        ],
        nav: navShow('partsflow', 'predict'),
        status: 'drafts',
      },
      {
        what: 'Enhance technician guidance',
        where: 'Knowledge articles from remote sessions',
        figure: [
          fx('B.kb.length'),
          ft(' articles attached to job cards on the same component and model'),
        ],
        nav: navShow('remote'),
        status: 'live',
      },
      {
        what: 'Feed product engineering',
        where: 'Bulletins · design notes',
        figure: [
          ft(
            'connector routing off the wash-down path (ML-21E) · hose routing at the frame crossing (ML-21)',
          ),
        ],
        nav: navShow('data'),
        status: 'live',
      },
      {
        what: 'Optimise inventory policy',
        where: 'Network minimums',
        figure: [
          ft('Newcastle DC minimum for front-end kits raised · Townsville to hold battery modules'),
        ],
        nav: navShow('partsflow', 'inventory'),
        status: 'drafts',
      },
    ],
    panels: [
      {
        kind: 'table',
        eyebrow: 'Fleet rules now running — each came out of one job',
        cols: [{ label: 'Rule' }, { label: 'From' }, { label: 'Applies to' }],
        rowsExpr: 'FLEET_RULES',
      },
    ],
  },

  {
    id: 'commercial',
    n: 4,
    name: 'Commercial Opportunity Management',
    agent: 'Commercial',
    col: '#C8500A',
    icon: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5"/>',
    purpose:
      'The installed base is read as a market: what each machine could be worth in parts, service and contracts, where the white space is, which contracts to upgrade and which renewals are at risk, what additional work each job reveals — and whether any of it converts.',
    items: [
      {
        what: 'Calculate aftermarket potential',
        where: 'Parts and service per machine-year',
        figure: [
          fx('fmtk(revenue().parts12)'),
          ft(' of parts in 12 months · '),
          fx('fmtk(revenue().labour10w)'),
          ft(' of Meridian technician labour in ten weeks'),
        ],
        nav: navShow('costs'),
        status: 'live',
      },
      {
        what: 'Identify white space',
        where: 'Call-off contracts and contractor diesel fleets',
        figure: [
          fx("FLEET.filter(f=>/call-off/.test(mxOf(f.machine_id)?.servicecontract?.type||'')&&!(f.battery)).length"),
          ft(' machines on call-off terms (Kalbarri, Mount Read diesel fleet)'),
        ],
        nav: navShow('customers'),
        status: 'live',
      },
      {
        what: 'Recommend contract upgrades',
        where: 'Upgrade candidates',
        figure: [
          fx('upgradeCandidates().length'),
          ft(
            ' call-off machines in poor condition or worse — Kalbarri after the second early coupling',
          ),
        ],
        nav: navShow('module', 'commercial'),
        status: 'live',
      },
      {
        what: 'Predict renewal likelihood',
        where: 'Renewals · CSS · availability',
        figure: [
          fx('renewals()[0].contractnum'),
          ft(' ('),
          fx('renewals()[0].customer'),
          ft(') ends in '),
          fx('renewals()[0].days'),
          ft(' days — CSS and availability support renewal'),
        ],
        nav: navShow('customers'),
        status: 'live',
      },
      {
        what: 'Target additional sales (parts, service, rebuilds, upgrades)',
        where: 'Exchange units · campaigns',
        figure: [
          fx('EXCHANGE.length'),
          ft(' exchange units · hose campaign parts for 3 more loaders · thermal sleeves on the ML-21 fleet'),
        ],
        nav: navShow('partsflow'),
        status: 'live',
      },
      {
        what: 'Track conversion and value',
        where: 'Opportunities → orders',
        figure: [ft('conversion tracking from proposal to order on the roadmap')],
        nav: navShow('customers'),
        status: 'road',
      },
    ],
    panels: [
      {
        kind: 'table',
        eyebrow: 'Contracts · renewal horizon',
        cols: [
          { label: 'Contract' },
          { label: 'Customer' },
          { label: 'Type' },
          { label: 'Machines', numeric: true },
          { label: 'Ends' },
          { label: 'Days', numeric: true },
          { label: 'Read' },
        ],
        rowsExpr: 'renewals()',
        literals: ['renewal proposal drafted', 'upgrade candidate', '—'],
      },
      {
        kind: 'table',
        eyebrow: 'Upgrade candidates · call-off machines in poor condition or worse',
        cols: [
          { label: 'Machine' },
          { label: 'Site' },
          { label: 'Condition' },
          { label: 'Worst component' },
          { label: 'Current terms' },
          { label: 'Proposal' },
        ],
        rowsExpr: 'upgradeCandidates()',
        literals: [
          'Fleet Care (PM + condition monitoring) — the two coupling failures were both practice',
          'Bring the diesel fleet under Battery fleet care terms',
        ],
      },
    ],
  },

  {
    id: 'planning',
    n: 5,
    name: 'Demand & Supply Planning',
    agent: 'Demand Planning',
    col: '#2F5FA8',
    icon: '<circle cx="6" cy="6" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="12" cy="18" r="2.5"/><path d="M8 7l3 9M16 7l-3 9M8.5 6h7"/>',
    purpose:
      'What the fleet will need in parts, service and components is forecast from the demand the modules sense, and positioned across the network: which echelon holds what, what to procure or transfer, and whether the technicians and workshops can carry it.',
    items: [
      {
        what: 'Forecast parts, service and component demand',
        where: 'Predicted usage · PMs due',
        figure: [
          fx('predictedUsage().reduce((a,r)=>a+r.demand,0)'),
          ft(' part units inside failure windows · '),
          fx('pmDue(30).length'),
          ft(' PMs due in 30 days'),
        ],
        nav: navShow('partsflow', 'predict'),
        status: 'live',
      },
      {
        what: 'Multi-echelon inventory planning',
        where: 'Site store → DC → other DCs',
        figure: [
          fx('[...new Set(B.inventory.map(i=>i.location))].length'),
          ft(' stocking locations across two echelons'),
        ],
        nav: navShow('partsflow', 'inventory'),
        status: 'live',
      },
      {
        what: 'Optimise stock positioning',
        where: 'Minimum-level proposals',
        figure: [
          fx('predictedUsage().length'),
          ft(' proposals · Newcastle DC front-end kits · Townsville battery modules'),
        ],
        nav: navShow('partsflow', 'predict'),
        status: 'drafts',
      },
      {
        what: 'Plan procurement and transfers',
        where: 'Requisitions · DC transfers',
        figure: [
          fx('B.orders.filter(o=>/agent/i.test(o.status)).length'),
          ft(' agent requisitions · '),
          fx('predictedUsage().filter(r=>/Transfer/.test(r.prop)).length'),
          ft(' transfers proposed'),
        ],
        nav: navShow('partsflow'),
        status: 'drafts',
      },
      {
        what: 'Forecast technician and workshop capacity',
        where: 'Roster W37 · utilisation',
        figure: [
          fx('B.roster.rows.length'),
          ft(' technicians on the roster · '),
          fx('meanUtil()'),
          ft('% mean utilisation · workshop capacity on the roadmap'),
        ],
        nav: navShow('techs'),
        status: 'live',
      },
      {
        what: 'Scenario modelling (what-if)',
        where: 'Ask the assistant',
        figure: [
          ft(
            'what-if on windows, freight and crews — on the roadmap (the assistant answers single questions today)',
          ),
        ],
        nav: navAsk('What happens if I wait on T-09?'),
        status: 'road',
      },
    ],
    panels: [
      {
        kind: 'table',
        eyebrow: 'Predicted usage against the shelf · next 30 days',
        cols: [
          { label: 'Part' },
          { label: 'Site' },
          { label: 'Predicted', numeric: true },
          { label: 'Site store', numeric: true },
          { label: 'DC', numeric: true },
          { label: 'Agent proposal' },
        ],
        rowsExpr: 'predictedUsage().slice(0,12)',
      },
    ],
  },
];

/** `LOOPS` keyed by id — the demo's `LOOP_BY` (line 2939). */
export const LOOP_BY_ID: Readonly<Record<LoopId, OemLoop>> = Object.fromEntries(
  LOOPS.map((l) => [l.id, l]),
) as Record<LoopId, OemLoop>;
